import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, MapPin } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", interest: "", message: "" };

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.errorTitle, { description: t.contact.errorBody });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, locale: lang });
      setSuccess(true);
      setForm(initial);
      toast.success(t.contact.successTitle, { description: t.contact.successBody });
    } catch (err) {
      toast.error(t.contact.errorTitle, { description: t.contact.errorBody });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="bg-[#070E13] text-[#F4EEE8] pt-40 pb-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <Reveal>
            <p className="overline gold-text">{t.contact.eyebrow}</p>
            <h1 className="h-editorial text-5xl sm:text-6xl lg:text-7xl mt-6 max-w-4xl">{t.contact.title}</h1>
            <p className="mt-8 max-w-2xl text-lg text-[#F4EEE8]/75">{t.contact.lead}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#070E13] py-20 sm:py-28 border-t border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* FORM */}
          <Reveal className="lg:col-span-7">
            {success ? (
              <div data-testid="contact-success" className="bg-[#0C1821] text-[#F4EEE8] p-10 sm:p-14 border border-[#C7A26C]/30">
                <p className="overline gold-text">✦ {t.contact.successTitle}</p>
                <p className="font-serif text-3xl sm:text-4xl mt-6">{t.contact.successBody}</p>
                <button
                  data-testid="send-another-button"
                  onClick={() => setSuccess(false)}
                  className="mt-10 text-xs uppercase tracking-[0.2em] text-[#C7A26C] link-underline"
                >
                  ← Send another
                </button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field label={t.contact.formName} name="name" value={form.name} onChange={handle} testid="input-name" required />
                  <Field label={t.contact.formEmail} name="email" type="email" value={form.email} onChange={handle} testid="input-email" required />
                  <Field label={t.contact.formPhone} name="phone" value={form.phone} onChange={handle} testid="input-phone" />
                  <SelectField
                    label={t.contact.formInterest}
                    name="interest"
                    value={form.interest}
                    onChange={handle}
                    options={t.contact.interests}
                    testid="input-interest"
                  />
                </div>
                <div>
                  <label className="overline text-[#D6D1C9]/70 block mb-3" htmlFor="message">
                    {t.contact.formMessage}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="input-message"
                    value={form.message}
                    onChange={handle}
                    rows={5}
                    required
                    className="w-full bg-transparent border-b border-[#C7A26C]/30 focus:border-[#C7A26C] outline-none py-3 text-[#F4EEE8] resize-none transition-colors"
                  />
                </div>
                <div className="flex flex-wrap gap-4 items-center pt-4">
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={submitting}
                    className="cta-premium bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] px-9 py-4 text-xs tracking-[0.24em] uppercase font-medium disabled:opacity-50"
                  >
                    {submitting ? t.contact.formSending : t.contact.formSubmit}
                  </button>
                  <a
                    data-testid="form-whatsapp-link"
                    href="https://wa.me/971558441468"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-8 py-4 text-xs tracking-[0.2em] uppercase border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-[#070E13] transition-colors"
                  >
                    <MessageCircle size={16} /> {t.contact.whatsappCta}
                  </a>
                </div>
              </form>
            )}
          </Reveal>

          {/* DIRECT INFO */}
          <Reveal delay={150} className="lg:col-span-5 lg:pl-10 lg:border-l lg:border-[#C7A26C]/20">
            <p className="overline gold-text">{t.contact.directLineLabel}</p>
            <ul className="mt-8 space-y-7 text-[#F4EEE8]">
              <li className="flex gap-4 items-start">
                <Mail size={18} className="mt-1 text-[#C7A26C]" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#D6D1C9]/60">{t.contact.emailLabel}</p>
                  <a data-testid="contact-email" href={`mailto:${t.contact.directEmail}`} className="font-serif text-2xl link-underline">
                    {t.contact.directEmail}
                  </a>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <Phone size={18} className="mt-1 text-[#C7A26C]" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#D6D1C9]/60">{t.common.whatsappLine}</p>
                  <a data-testid="contact-phone" href="https://wa.me/971558441468" target="_blank" rel="noreferrer" className="font-serif text-2xl link-underline">
                    {t.contact.directPhone}
                  </a>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <MapPin size={18} className="mt-1 text-[#C7A26C]" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#D6D1C9]/60">{t.contact.regionsLabel}</p>
                  <p className="font-serif text-2xl">{t.contact.regions}</p>
                </div>
              </li>
            </ul>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="overline text-[#D6D1C9]/70 block mb-3" htmlFor={name}>
        {label}{required && <span className="text-[#DE3C30] ms-1">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border-b border-[#C7A26C]/30 focus:border-[#C7A26C] outline-none py-3 text-[#F4EEE8] transition-colors"
      />
    </div>
  );
}

function SelectField({ label, name, value, onChange, options, testid }) {
  return (
    <div>
      <label className="overline text-[#D6D1C9]/70 block mb-3" htmlFor={name}>{label}</label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-transparent border-b border-[#C7A26C]/30 focus:border-[#C7A26C] outline-none py-3 text-[#F4EEE8] transition-colors"
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
